import React, { useContext, useEffect, useState } from "react";
import {
  PFieldsetWrapper,
  PTextFieldWrapper,
} from "@porsche-design-system/components-react";
import { spacingFluidSmall } from "@porsche-design-system/components-js/styles";
import { useTranslation } from "react-i18next";
import isoCountries from "i18n-iso-countries";
import enLocaleData from "i18n-iso-countries/langs/en.json";
import esLocaleData from "i18n-iso-countries/langs/es.json";
import frLocaleData from "i18n-iso-countries/langs/fr.json";
import itLocaleData from "i18n-iso-countries/langs/it.json";
import zhLocaleData from "i18n-iso-countries/langs/zh.json";
import { AuthContext } from "MainApp/assets/utils/context/AuthContext";
import { LanguageContext } from "MainApp/assets/utils/context/LanguageContext";
import { getSupportedLanguageCode } from "MainApp/assets/utils/js/localeUtils";
import useTexts from "MainApp/assets/utils/hooks/useTexts";
import CountrySelectCustom from "./CountrySelectCustom";
import CustomPickup from "../CustomPickup";
import PhoneNumberInput from "../PhoneNumberInput/PhoneNumberInput.jsx";
import SpokenLanguage from "./SpokenLanguage";
import { resolveCustomPickupConfig } from "../customPickupPricing";
import "./PersonalInfoFields.css";

const COUNTRY_LOCALES = {
  en: enLocaleData,
  es: esLocaleData,
  fr: frLocaleData,
  it: itLocaleData,
  zh: zhLocaleData,
};

const loadAndRegisterLanguage = (langCode) => {
  const code = getSupportedLanguageCode(langCode);
  const localeData = COUNTRY_LOCALES[code] || enLocaleData;

  isoCountries.registerLocale(localeData);
  return COUNTRY_LOCALES[code] ? code : "en";
};

export const getCountryNames = (languageCode) => {
  const effectiveCode = loadAndRegisterLanguage(languageCode);
  const names = isoCountries.getNames(effectiveCode, { select: "official" });

  if (!names || Object.keys(names).length === 0) {
    isoCountries.registerLocale(enLocaleData);
    const fallbackNames = isoCountries.getNames("en", { select: "official" });
    return Object.entries(fallbackNames).map(([code, label]) => ({
      value: code,
      label,
    }));
  }

  return Object.entries(names).map(([code, label]) => ({
    value: code,
    label,
  }));
};

const PersonalInfoFields = ({
  formData,
  setFormData,
  textBranch,
  textOrigin,
  phoneRef,
  onPhoneChange,
  countryCodeRef,
  phoneTexts,
  textHeader,
  isPhone,
  onError,
}) => {
  const { language: languageCodeRaw } = useContext(LanguageContext);
  const languageCode = getSupportedLanguageCode(languageCodeRaw);
  const { optionalFields, userData } = useContext(AuthContext);

  const [countryList, setCountryList] = useState([]);
  const [firstNameError, setFirstNameError] = useState(false);
  const [lastNameError, setLastNameError] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [countryError, setCountryError] = useState(false);
  const [firstNameTouched, setFirstNameTouched] = useState(false);
  const [lastNameTouched, setLastNameTouched] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);
  const [countryTouched] = useState(false);
  const [isRetroTour, setIsRetroTour] = useState(false);

  useEffect(() => {
    try {
      const list = getCountryNames(languageCode);
      setCountryList(list);
    } catch (error) {
      console.error("Error en getCountryNames:", error);
      const fallbackNames = isoCountries.getNames("en", { select: "official" });
      const fallback = Object.entries(fallbackNames).map(([code, label]) => ({
        value: code,
        label,
      }));
      setCountryList(fallback);
    }
  }, [languageCode]);

  useEffect(() => {
    if (isRetroTour) {
      setFormData((prevFormData) => ({
        ...prevFormData,
        lastName: prevFormData.lastName || " ",
      }));
    }
  }, [isRetroTour, setFormData]);

  useEffect(() => {
    setFirstNameError(!formData?.firstName?.trim() && firstNameTouched);
    setLastNameError(!formData?.lastName?.trim() && lastNameTouched);
    setEmailError(!formData?.email?.trim() && emailTouched);
    setCountryError(!formData?.country?.trim() && countryTouched);
  }, [
    formData.firstName,
    formData.lastName,
    formData.email,
    formData.country,
    firstNameTouched,
    lastNameTouched,
    emailTouched,
    countryTouched,
  ]);

  useEffect(() => {
    if (onError) {
      onError({
        firstNameError,
        lastNameError,
        emailError,
        countryError,
      });
    }
  }, [firstNameError, lastNameError, emailError, countryError, onError]);

  const { t } = useTranslation(textOrigin);
  const { t: tCommon } = useTranslation("common");
  const tt = (field) => t(textBranch + field);
  const requiredFieldMessage =
    tt(".validation.required") || tCommon("requiredField");
  const textOrigin2 = "confirmationScreen";
  const { texts } = useTexts(textOrigin2);
  const customPickupConfig = resolveCustomPickupConfig({
    optionalFields,
    formData,
  });

  const contentFirst = (
    <>
      <PTextFieldWrapper
        className="w-full"
        label={tt(".name")}
        state={firstNameError && firstNameTouched ? "error" : undefined}
        message={
          firstNameError && firstNameTouched
            ? requiredFieldMessage
            : undefined
        }
      >
        <input
          type="text"
          value={formData.firstName || ""}
          onInput={(event) =>
            setFormData({ ...formData, firstName: event.target.value })
          }
          required
          onBlur={() => {
            setFirstNameTouched(true);
            setFirstNameError(!formData?.firstName?.trim());
          }}
        />
      </PTextFieldWrapper>

      {!isRetroTour && (
        <PTextFieldWrapper
          className="w-full"
          label={tt(".surname")}
          state={lastNameError && lastNameTouched ? "error" : undefined}
          message={
            lastNameError && lastNameTouched
              ? requiredFieldMessage
              : undefined
          }
        >
          <input
            type="text"
            value={formData.lastName || ""}
            onInput={(event) =>
              setFormData({ ...formData, lastName: event.target.value })
            }
            required
            onBlur={() => {
              setLastNameTouched(true);
              setLastNameError(!formData?.lastName?.trim());
            }}
          />
        </PTextFieldWrapper>
      )}

      {customPickupConfig.enabled && (
        <CustomPickup
          formData={formData}
          setFormData={setFormData}
          textHeader={texts("form.customPickup.header")}
          pickupConfig={customPickupConfig}
        />
      )}
    </>
  );

  const handleCountryChange = (newValue) => {
    setFormData({ ...formData, country: newValue });
  };

  useEffect(() => {
    const checkIframeOrigin = () => {
      try {
        setIsRetroTour(userData.data.companyName === "Retro Tour Madrid");
      } catch (error) {
        console.error("Error checking origin:", error);
        setIsRetroTour(false);
      }
    };

    checkIframeOrigin();
  }, [userData]);

  return (
    <PFieldsetWrapper label={textHeader}>
      <div className="flex flex-col" style={{ gap: spacingFluidSmall }}>
        {!isPhone ? (
          <div
            className="flex flex-col w-full"
            style={{ gap: spacingFluidSmall }}
          >
            {contentFirst}
          </div>
        ) : (
          contentFirst
        )}

        <PTextFieldWrapper
          label={tt(".mail")}
          state={emailError && emailTouched ? "error" : undefined}
          message={
            emailError && emailTouched ? requiredFieldMessage : undefined
          }
        >
          <input
            type="email"
            value={formData.email || ""}
            onInput={(event) =>
              setFormData({ ...formData, email: event.target.value })
            }
            required
            onBlur={() => {
              setEmailTouched(true);
              setEmailError(!formData?.email?.trim());
            }}
          />
        </PTextFieldWrapper>

        {!isRetroTour ? (
          <CountrySelectCustom
            countryList={countryList}
            value={formData.country || ""}
            onChange={handleCountryChange}
            placeholder={tt(".country.ph") || "Select a country"}
            label={tt(".country.head") || "Country"}
            isDisabled={countryList.length === 0}
          />
        ) : (
          <SpokenLanguage
            value={formData.country || ""}
            onChange={handleCountryChange}
            placeholder={tt(".spoken.ph") || "Select a language"}
            label={tt(".spoken.head") || "Language"}
          />
        )}

        <PhoneNumberInput
          phoneRef={phoneRef}
          countryCodeRef={countryCodeRef}
          onPhoneChange={onPhoneChange}
          textOrigin={textOrigin}
          textBranch={phoneTexts}
          isPhone={isPhone}
          onError={(phoneErrors) => {
            if (onError) {
              onError({
                firstNameError,
                lastNameError,
                emailError,
                countryError,
                ...phoneErrors,
              });
            }
          }}
        />
      </div>
    </PFieldsetWrapper>
  );
};

export default PersonalInfoFields;
